<template>
  <woot-modal :show.sync="show" :on-close="onClose" size="modal-big">
    <woot-modal-header
      :header-title="$t('WHATSAPP_TEMPLATES.MODAL.TITLE')"
      :header-content="modalHeaderContent"
    />

    <div class="row modal-content">
      <div
        v-if="contact && contact.blocked_contact"
        class="alert-filter wide-alert"
      >
        <div class="icon-container">
          <fluent-icon icon="warning" size="40" />
        </div>

        <div class="alert-text">
          <h3>
            {{ $t('NEW_CONVERSATION.BLOCKED_CONTACT_ALERT.WARNING') }}
          </h3>

          <p v-html="$t('NEW_CONVERSATION.BLOCKED_CONTACT_ALERT.TEXT')"></p>
        </div>
      </div>

      <div
        v-else-if="contact && contact.blocked_from_templates"
        class="alert-filter wide-alert"
      >
        <div class="icon-container">
          <fluent-icon icon="warning" size="40" />
        </div>

        <div class="alert-text">
          <h3>
            {{ $t('NEW_CONVERSATION.BLOCKED_TEMPLATES_ALERT.WARNING') }}
          </h3>

          <p v-html="$t('NEW_CONVERSATION.BLOCKED_TEMPLATES_ALERT.TEXT')"></p>
        </div>
      </div>
      <div v-else-if="whatsappTemplatesUiFlags.isFetchingBlockedPerLimit">
        <spinner :colorScheme="'dark'" />
      </div>
      <div
        v-else-if="messageTemplateLimitExceeded"
        class="alert-filter wide-alert"
      >
        <div class="icon-container">
          <fluent-icon icon="warning" size="40" />
        </div>

        <div class="alert-text">
          <p v-html="messageTemplateLimitExceeded"></p>
        </div>
      </div>

      <templates-picker
        v-else-if="!selectedWaTemplate"
        :inbox-id="inboxId"
        :template-filter="templateFilters"
        @onSelect="pickTemplate"
      />

      <template-parser
        v-else
        :template="selectedWaTemplate"
        :enable-parameterization="true"
        :inbox-id="inboxId"
        @resetTemplate="onResetTemplate"
        @sendMessage="onSendMessage"
      />
    </div>
  </woot-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import TemplatesPicker from './TemplatesPicker.vue';
import TemplateParser from './TemplateParser.vue';
import Spinner from 'shared/components/Spinner.vue';

export default {
  components: {
    TemplatesPicker,
    TemplateParser,
    Spinner,
  },
  props: {
    inboxId: {
      type: Number,
      default: undefined,
    },
    show: {
      type: Boolean,
      default: true,
    },
    selectedTemplate: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedWaTemplate: null,
    };
  },
  watch: {
    selectedTemplate: {
      immediate: true,
      handler(newTemplate) {
        if (newTemplate) {
          this.selectedWaTemplate = newTemplate;
        }
      },
    },
    show: {
      immediate: true,
      handler(newTemplate) {
        if (newTemplate) {
          if(this.currentAccount?.enable_template_limit || this.currentUserAccount.enable_template_limit){
            this.$store.dispatch('whatsappTemplates/getBlockedSendTemplates');
          }
        }
      },
    },
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      contacts: 'contacts/getContacts',
      currentAccount: 'getCurrentAccount',
      currentUserAccount: 'getCurrentUserAccount',
      blockedSendTemplates: 'whatsappTemplates/getBlockedSendTemplates',
      whatsappTemplatesUiFlags: 'whatsappTemplates/getUIFlags',
    }),
    contactId() {
      return this.currentChat?.meta?.sender?.id;
    },
    contact() {
      if (!this.contactId) return null;
      return this.contacts?.find(c => c.id === this.contactId);
    },
    templateFilters() {
      return {
        marketing: true,
        utility: true,
      };
    },
    modalHeaderContent() {
      return this.selectedWaTemplate
        ? this.$t('WHATSAPP_TEMPLATES.MODAL.TEMPLATE_SELECTED_SUBTITLE', {
            templateName: this.selectedWaTemplate.name,
          })
        : this.$t('WHATSAPP_TEMPLATES.MODAL.SUBTITLE');
    },
    messageTemplateLimitExceeded(){
      if(!this.blockedSendTemplates || !this.currentAccount || !this.currentUserAccount || Object.keys(this.blockedSendTemplates).length === 0){
        return null;
      }
      if(this.blockedSendTemplates?.user.daily){
        return this.$t('CONVERSATION.BLOCKED_TEMPLATE_LIMIT.AGENT.DAILY', { limit: this.currentUserAccount?.daily_template_limit });
      }
      if(this.blockedSendTemplates?.user.monthly){
        return this.$t('CONVERSATION.BLOCKED_TEMPLATE_LIMIT.AGENT.MONTHLY', { limit: this.currentUserAccount?.monthly_template_limit });
      }
      if(this.blockedSendTemplates?.account.daily){
        return this.$t('CONVERSATION.BLOCKED_TEMPLATE_LIMIT.ACCOUNT.DAILY', { limit: this.currentAccount?.daily_template_limit });
      }
      if(this.blockedSendTemplates.account.monthly){
        return this.$t('CONVERSATION.BLOCKED_TEMPLATE_LIMIT.ACCOUNT.MONTHLY', { limit: this.currentAccount?.monthly_template_limit });
      }
      return null;
    }
  },
  methods: {
    pickTemplate(template) {
      if (this.contact && this.contact.blocked_from_templates) return;
      this.selectedWaTemplate = template;
    },
    onResetTemplate() {
      this.selectedWaTemplate = null;
    },
    onSendMessage(message) {
      if (this.contact && this.contact.blocked_from_templates) return;
      this.$emit('on-send', message);
    },
    onClose() {
      this.$emit('cancel');
    },
  },
};
</script>

<style scoped lang="scss">
.modal-content {
  padding: 2.5rem 3.2rem;
}

.wide-alert {
  width: 100%;
  max-width: 100%;
  padding-right: 50px;
  display: flex;
}

.alert-filter {
  width: 100%;
  max-width: 100%;
  flex: 1 1 auto !important;
  display: flex;
  align-items: flex-start;
  padding: var(--space-small);
  background: var(--r-50);
  border-radius: var(--border-radius-small);
  margin-bottom: var(--space-medium);
  margin-right: -50px;

  .icon-container {
    flex-shrink: 0;
    margin-right: var(--space-small);

    svg {
      color: var(--r-800);
    }
  }

  .alert-text {
    flex-grow: 1;
    min-width: 0;

    h3 {
      font-size: var(--font-size-default);
      font-weight: var(--font-weight-black);
    }

    p {
      font-size: var(--font-size-mini);
      line-height: 1.5;
      margin-bottom: 0;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
  }
}
</style>
